import { NgClass, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DrEleksVisit } from '@lib/core/models/dobrobut-entities.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { VisitsService } from '../../../../core/services/visits.service';
import { EleksVisitComponent } from './eleks-visit/eleks-visit.component';

export enum EleksVisitsWarnings {
  NoVisits = 'Увага! Візити для цього пацієнта відсутні в Добробуті',
  Undefined = 'Не вдалось отримати візити з Добробуту',
  NoDoctorId = 'Не вдалося отримати Doctor ID з Добробуту. Запис не знайдено в базі lilo',
  NoActionData = 'Не вдалося отримати дані про незапланований візит',
}

@Component({
  selector: 'hm-eleks-visits-list',
  standalone: true,
  imports: [LoaderComponent, EleksVisitComponent, NgOptimizedImage, NgClass],
  templateUrl: './eleks-visits-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EleksVisitsListComponent implements OnInit {
  private readonly visitsService = inject(VisitsService);
  private readonly destroyRef = inject(DestroyRef);

  // TODO: uncomment and rollback filter after needed visits will be synchronized
  // protected readonly today = new Date();

  userId = input.required<string>();

  protected loading = signal<boolean>(false);
  protected eleksVisits = signal<DrEleksVisit[]>([]);
  protected eleksVisitsWarning = signal<string | null>(null);

  protected get selectedVisit(): DrEleksVisit {
    return this.visitsService.selectedEleksVisit$.value;
  }

  ngOnInit(): void {
    this.visitsService.selectedEleksVisit$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(visit => {
      if (visit) {
        this.eleksVisits.set([visit]);
      } else {
        this.getEleksVisitsData(this.userId());
      }
    });
  }

  protected getEleksVisitsData(userId: string) {
    this.loading.set(true);
    return this.visitsService
      .getDrEleksVisits(userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: visits => {
          // TODO: uncomment and rollback filter after needed visits will be synchronized
          // const filteredVisits = visits.filter(visit => !visit.usedInAppointmentId && new Date(visit.startDate) > this.today);
          const filteredVisits = visits.filter(visit => !visit.usedInAppointmentId);

          this.eleksVisits.set([...filteredVisits]);

          if (!this.eleksVisits().length) {
            this.eleksVisitsWarning.set(EleksVisitsWarnings.NoVisits);
          }
        },
        error: () => {
          this.eleksVisits.set([]);
          this.eleksVisitsWarning.set(EleksVisitsWarnings.Undefined);
          this.loading.set(false);
        },
        complete: () => this.loading.set(false),
      });
  }
}
